import React from 'react';
import type { PieSvgProps } from '@nivo/pie';

/**
 * A single datum for the pie chart.
 */
export interface PieDatum {
  /** Unique identifier for the slice */
  id: string | number;
  /** Label for the slice */
  label: string;
  /** Value for the slice */
  value: number;
  /** Optional color override */
  color?: string;
}

/**
 * Supported formats for arc labels and tooltips.
 */
export type PieFormat = 'percentage' | 'dollars' | 'currency' | 'number';

/**
 * Supported legend positions.
 */
export type LegendPosition = 'below';

/**
 * Common props for both responsive and static pies.
 */
interface PieBaseProps {
  /** Data for the pie chart */
  data: PieDatum[];
  /** Optional CSS class */
  className?: string;
  /** Optional style object */
  style?: React.CSSProperties;
  /** Show arc labels on the chart */
  showArcLabels?: boolean;
  /** Format for arc labels */
  arcLabelFormat?: PieFormat;
  /** Format for tooltip values */
  tooltipFormat?: PieFormat;
  /** Arc link label type */
  arcLinkLabelType?: 'name' | 'value';
  /** Show legend */
  showLegend?: boolean;
  /** Legend position (only relevant if showLegend is true) */
  legendPosition?: LegendPosition;
  /** Optional totals display element */
  totals?: React.ReactNode;
}

export type ResponsivePieProps = PieBaseProps & {
  responsive: true;
} & Omit<Partial<PieSvgProps<PieDatum>>, 'height' | 'width'>;

export type StaticPieProps = PieBaseProps & {
  responsive?: false;
  height: number;
  width: number;
} & Omit<Partial<PieSvgProps<PieDatum>>, 'height' | 'width'>;

export type PieProps = ResponsivePieProps | StaticPieProps;

export function isStaticPieProps(props: PieProps): props is StaticPieProps {
  return props.responsive !== true;
}
